"use client";

import { useMemo } from "react";
import type { SessionListItem, FolderResponse } from "@/app/lib/types";
import SessionItem from "./SessionItem";

interface RecentTabProps {
  sessions: SessionListItem[];
  folders: FolderResponse[];
  activeSessionId?: string;
  onSessionClick: (id: string) => void;
  onSessionDelete: (id: string) => void;
  onSessionRename: (id: string, title: string) => void;
  onSessionFlag: (id: string) => void;
  onMoveToFolder?: (id: string) => void;
  onMoveSessionToFolder?: (sessionId: string, folderId: string) => void;
  onMoveToNewFolder?: (sessionId: string) => void;
  onShare?: (id: string) => void;
  currentUserEmail?: string;
  selectionMode?: boolean;
  selectedIds?: Set<string>;
  onToggleSelect?: (id: string) => void;
}

export default function RecentTab({
  sessions,
  folders,
  activeSessionId,
  onSessionClick,
  onSessionDelete,
  onSessionRename,
  onSessionFlag,
  onMoveToFolder,
  onMoveSessionToFolder,
  onMoveToNewFolder,
  onShare,
  currentUserEmail,
  selectionMode,
  selectedIds,
  onToggleSelect,
}: RecentTabProps) {
  const sorted = useMemo(() => {
    // Real "last chat date" = last_message_at (the most recent message), NOT
    // updated_at — which metadata touches (folder move, flag, rename) bump.
    const lastActivity = (s: (typeof sessions)[number]) =>
      new Date(s.last_message_at || s.updated_at).getTime();
    return [...sessions].sort((a, b) => {
      // Flagged chats pin to the top; within each group, most-recent message first.
      const flagDiff = (b.flagged ? 1 : 0) - (a.flagged ? 1 : 0);
      if (flagDiff !== 0) return flagDiff;
      const diff = lastActivity(b) - lastActivity(a);
      if (diff !== 0) return diff;
      return (
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      );
    });
  }, [sessions]);

  if (sorted.length === 0) {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "40px 16px",
          color: "var(--text-tertiary)",
          fontSize: "13px",
        }}
      >
        No conversations yet
      </div>
    );
  }

  // TODO: Future enhancement — drag-reorder sessions using HTML5 drag-and-drop
  // (similar to BottomToolbar provider reordering). Would need drag handles,
  // onDragStart/onDragOver/onDrop handlers, and updateSession(id, { order }) calls.

  return (
    <div>
      {sorted.map((session) => (
        <SessionItem
          key={session.id}
          session={session}
          isActive={activeSessionId === session.id}
          onClick={() => onSessionClick(session.id)}
          onDelete={onSessionDelete}
          onRename={onSessionRename}
          onFlag={onSessionFlag}
          onMoveToFolder={onMoveToFolder}
          onMoveSessionToFolder={onMoveSessionToFolder}
          onMoveToNewFolder={onMoveToNewFolder}
          folders={folders}
          onShare={onShare}
          currentUserEmail={currentUserEmail}
          folderName={session.folder_id ? folders.find(f => f.id === session.folder_id)?.name : undefined}
          selectionMode={selectionMode}
          selected={selectedIds?.has(session.id)}
          onToggleSelect={onToggleSelect}
        />
      ))}
    </div>
  );
}
